import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Spinner, Alert, Form, InputGroup, Pagination, Badge, Image } from 'react-bootstrap';
import * as adminApi from '../../Services/adminApi';
import { useDebounce } from 'use-debounce';
import { Link } from 'react-router-dom';

const ManageListings = () => {
    const [listings, setListings] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    // Filters
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm] = useDebounce(searchTerm, 500);
    const [filters, setFilters] = useState({ categoryId: '', status: 'all' });

    const fetchListings = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                page: currentPage,
                limit: 10,
                searchTerm: debouncedSearchTerm,
                ...filters,
            };
            const data = await adminApi.getListings(params);
            setListings(data.listings);
            setTotalPages(data.totalPages);
            setError('');
        } catch (err) {
            setError('Failed to fetch listings.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [currentPage, debouncedSearchTerm, filters]);

    // Fetch categories for the filter dropdown
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const cats = await adminApi.getCategories();
                setCategories(cats);
            } catch (err) {
                console.error("Failed to fetch categories for filter", err);
            }
        };
        fetchCategories();
    }, []);

    useEffect(() => {
        fetchListings();
    }, [fetchListings]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
        setCurrentPage(1);
    };

    const handleDeleteListing = async (listingId, title) => {
        if (window.confirm(`Are you sure you want to delete the listing "${title}"?`)) {
            try {
                await adminApi.deleteListing(listingId);
                // Refresh the list after deletion
                fetchListings();
            } catch (err) {
                alert('Failed to delete listing.');
            }
        }
    };

    const getStatusBadge = (status) => {
        switch (status.toLowerCase()) {
            case 'na voljo': return <Badge bg="success">Na voljo</Badge>;
            case 'prodano': return <Badge bg="secondary">Prodano</Badge>;
            case 'pending_approval': return <Badge bg="warning" text="dark">Pending</Badge>;
            case 'rejected': return <Badge bg="danger">Rejected</Badge>;
            default: return <Badge bg="info">{status}</Badge>;
        }
    };

    return (
        <div>
            <h2>Listing Management</h2>
            <hr />

            <InputGroup className="mb-3">
                <InputGroup.Text><i className="fas fa-search"></i></InputGroup.Text>
                <Form.Control
                    placeholder="Search by title..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
                <Form.Select name="categoryId" value={filters.categoryId} onChange={handleFilterChange} style={{ maxWidth: '200px' }}>
                    <option value="">All Categories</option>
                    {categories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                </Form.Select>
                <Form.Select name="status" value={filters.status} onChange={handleFilterChange} style={{ maxWidth: '150px' }}>
                    <option value="all">All Statuses</option>
                    <option value="na voljo">Na voljo</option>
                    <option value="prodano">Prodano</option>
                    <option value="pending_approval">Pending</option>
                    <option value="rejected">Rejected</option>
                </Form.Select>
            </InputGroup>

            {loading && <div className="text-center"><Spinner animation="border" /></div>}
            {error && <Alert variant="danger">{error}</Alert>}

            {!loading && !error && (
                <>
                    <Table striped bordered hover responsive>
                        <thead>
                            <tr>
                                <th></th>
                                <th>Title</th>
                                <th>User</th>
                                <th>Category</th>
                                <th>Status</th>
                                <th>Created</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {listings.map(listing => (
                                <tr key={listing.id}>
                                    <td>
                                        <Image
                                            src={listing.images?.find(img => img.is_primary)?.image_url || 'https://via.placeholder.com/100'}
                                            thumbnail
                                            style={{ width: '75px', height: '75px', objectFit: 'cover' }}
                                        />
                                    </td>
                                    <td><Link to={`/addetail/${listing.id}`}>{listing.title}</Link></td>
                                    <td>{listing.user?.username}</td>
                                    <td>{listing.category?.name}</td>
                                    <td>{getStatusBadge(listing.status)}</td>
                                    <td>{new Date(listing.created_at).toLocaleDateString()}</td>
                                    <td>
                                        <Button variant="outline-danger" size="sm" onClick={() => handleDeleteListing(listing.id, listing.title)}>
                                            <i className="fas fa-trash"></i> Delete
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>

                    {totalPages > 1 && (
                        <Pagination className="justify-content-center">
                            <Pagination.Prev onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1} />
                            <Pagination.Item active>{currentPage} of {totalPages}</Pagination.Item>
                            <Pagination.Next onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages} />
                        </Pagination>
                    )}
                </>
            )}
        </div>
    );
};

export default ManageListings;